import { DateTime } from "luxon";

import { ENTP_MAPPINGS } from "@/configuration/document-mappings";
import entpInsuranceMappingData from "@/configuration/groups/entpartners/insurance-mapping.json";
import { entpartnersSchedulingConfig } from "@/configuration/groups/entpartners/scheduling/config";
import { AppointmentStatus } from "@/packages/shared/src/models/appointment";
import { DayOfWeek } from "@/packages/shared/src/models/scheduling-availability";
import { entpCptToStaff } from "@/src/data/entp-cpt-staff-mapping";
import { type AutoIntakeConfig } from "@/src/lib/auto-intake/auto-intake-config";
import { AutoIntakeBlockCheck } from "@/src/lib/auto-intake/auto-intake-eligibility-requirements";
import { ReferralRoutingStrategy } from "@/src/lib/constants";
import {
  buildPatientMetadataInsuranceVariables,
  resolveCoverageEcwId,
  type EcwInsuranceMapping,
  type EcwInsuranceMappingEntry,
  type PatientMetadataInsuranceVariables,
} from "@/src/lib/ecw-insurance-mapping";
import { EncounterType } from "@/src/lib/ehr/customer/encounter-filters";
import type { EcwEncounterExclusions } from "@/src/lib/ehr/ecw/ecw-webecw-helpers";
import { CobaltProviderAvailabilityPolicy } from "@/src/lib/ehr/scheduling/cobalt-provider-availability-rules";
import { tryGetEhrSystemId, tryGetMrn } from "@/src/lib/resolve-ehr-ids";
import { isCsvContentType } from "@/src/lib/utils/csv";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { isUuidSelected } from "@/src/lib/uuid";
import { AppointmentType } from "@/src/models/appointment";
import { Classification, ClassificationSection } from "@/src/models/constants/classifications";
import { DmeOrderType } from "@/src/models/constants/document-types";
import { DefaultWorkflow, Workflow, WorkflowType } from "@/src/models/constants/group";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Coverage, CoverageType } from "@/src/models/coverage";
import {
  PriorAuthorizationStatus,
  PriorAuthorizationType,
  type PriorAuthorization,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import type { Referral } from "@/src/models/documents/referral";
import { ClassificationPageDict } from "@/src/models/file";
import { Group } from "@/src/models/group";
import { isPatientNotInEhr, type Patient } from "@/src/models/patient";
import { PersonLanguage } from "@/src/models/person";
import { Section } from "@/src/models/section";
import { Task, TaskStatus } from "@/src/models/task";
import { RBACRole } from "@/src/models/user";

/**
 * Roles the Permissions Management tool may assign/remove for ENT Partners (live + its sandbox
 * sibling, which share this config). Exposes both the read-only analytics role
 * (`external_analytics_readonly`) and the writable `external_analytics`. Consumed by
 * `getAssignableRolesForGroup` in `configuration/index.ts`.
 */
export const ENTP_ASSIGNABLE_ROLES: readonly RBACRole[] = [
  RBACRole.EXTERNAL_ADMIN,
  RBACRole.EXTERNAL_ANALYTICS,
  RBACRole.EXTERNAL_ANALYTICS_READONLY,
  RBACRole.EXTERNAL_SCHEDULING,
];

// NOTE: Cobalt provider IDs are stable and currently used as practitioner IDs in this flow.
export const ENTP_PROVIDER_IDS = {
  MICHAEL_FRIEDMAN: "d238d113-df9c-493c-bff0-cfc5c535a873",
  JACOB_FRIEDMAN: "bf70873f-d3c2-45da-bf67-6adc0dc8eb4e",
  KATHRYN_COLMAN: "16395095-3dd8-4860-a266-8105cad1fc70",
  CLAIRE_KENNEALLY: "07d0aa70-780c-473e-b07c-e18bd9067308",
  ADAM_LEVY: "cff9e1cf-045f-4bec-b228-c5699f27703c",
  KATHLEEN_MASELLA: "f0dcd684-cb5a-462f-9fe6-d702897dc7fb",
  KIRK_VAN_SHEPARD: "84f6a79b-3bd0-410d-ab6a-a3fed3a45d04",
  TK_VENKATESAN: "a66be9d4-6100-4d5b-81a4-8cd002ca1d64",
  MALIK_NASSAN: "5418d983-c03d-4626-96a8-011434c4358f",
} as const;

export const ENTP_SANDBOX_PROVIDER_IDS = {
  MICHAEL_FRIEDMAN: "38803811-487b-4fb1-a344-c44342c79734",
  JACOB_FRIEDMAN: "ef1de7bb-3eba-485b-8a54-22fa697ff2c7",
  KATHRYN_COLMAN: "16395095-3dd8-4860-a266-8105cad1fc70",
  CLAIRE_KENNEALLY: "aec1bd8f-e95f-4fb9-af25-62352d43d929",
  ADAM_LEVY: "84e7c301-9829-4509-848c-79a24c198eb7",
  KATHLEEN_MASELLA: "f0dcd684-cb5a-462f-9fe6-d702897dc7fb",
  KIRK_VAN_SHEPARD: "4d52040e-a9ec-454d-8224-918aec99b233",
  TK_VENKATESAN: "11bfc518-38d0-48d9-a514-29024edc5477",
} as const;

export const ENTP_SANDBOX_COBALT_PROVIDER_AVAILABILITY_POLICIES: Record<string, CobaltProviderAvailabilityPolicy> = {
  [ENTP_SANDBOX_PROVIDER_IDS.MICHAEL_FRIEDMAN]: {
    defaultMaxSlotDurationMinutes: 10,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.JACOB_FRIEDMAN]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.KATHRYN_COLMAN]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.CLAIRE_KENNEALLY]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.ADAM_LEVY]: {
    defaultMaxSlotDurationMinutes: 10,
    disallowNewPatientInLastHour: true,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.KATHLEEN_MASELLA]: {
    defaultMaxSlotDurationMinutes: 20,
    maxSlotDurationMinutesByDay: {
      [DayOfWeek.THURSDAY]: 15,
    },
    // Tuesday afternoons are telehealth-only.
    telehealthOnlyWindows: [
      {
        dayOfWeek: DayOfWeek.TUESDAY,
        startHourInclusive: 12,
      },
    ],
  },
  [ENTP_SANDBOX_PROVIDER_IDS.KIRK_VAN_SHEPARD]: {
    defaultMaxSlotDurationMinutes: 30,
  },
  [ENTP_SANDBOX_PROVIDER_IDS.TK_VENKATESAN]: {
    defaultMaxSlotDurationMinutes: 10,
  },
};

export const ENTP_COBALT_PROVIDER_AVAILABILITY_POLICIES: Record<string, CobaltProviderAvailabilityPolicy> = {
  [ENTP_PROVIDER_IDS.MICHAEL_FRIEDMAN]: {
    defaultMaxSlotDurationMinutes: 10,
  },
  [ENTP_PROVIDER_IDS.JACOB_FRIEDMAN]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_PROVIDER_IDS.KATHRYN_COLMAN]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_PROVIDER_IDS.CLAIRE_KENNEALLY]: {
    defaultMaxSlotDurationMinutes: 15,
  },
  [ENTP_PROVIDER_IDS.ADAM_LEVY]: {
    defaultMaxSlotDurationMinutes: 10,
    disallowNewPatientInLastHour: true,
  },
  [ENTP_PROVIDER_IDS.KATHLEEN_MASELLA]: {
    defaultMaxSlotDurationMinutes: 20,
    maxSlotDurationMinutesByDay: {
      [DayOfWeek.THURSDAY]: 15,
    },
    // Tuesday afternoons are telehealth-only.
    telehealthOnlyWindows: [
      {
        dayOfWeek: DayOfWeek.TUESDAY,
        startHourInclusive: 12,
      },
    ],
  },
  [ENTP_PROVIDER_IDS.KIRK_VAN_SHEPARD]: {
    defaultMaxSlotDurationMinutes: 30,
  },
  [ENTP_PROVIDER_IDS.TK_VENKATESAN]: {
    defaultMaxSlotDurationMinutes: 10,
  },
  [ENTP_PROVIDER_IDS.MALIK_NASSAN]: {
    defaultMaxSlotDurationMinutes: 10,
  },
};

export const ENTP_COBALT_STATUS_CODE_MAP: Record<string, AppointmentStatus> = {
  "add on arr": AppointmentStatus.CHECKED_IN,
  "ansph": AppointmentStatus.SCHEDULED,
  "approved": AppointmentStatus.SCHEDULED,
  "arr": AppointmentStatus.CHECKED_IN,
  "canc": AppointmentStatus.CANCELLED,
  "cancphone": AppointmentStatus.CANCELLED,
  "cancsms": AppointmentStatus.CANCELLED,
  "cbo": AppointmentStatus.CANCELLED,
  "chk": AppointmentStatus.COMPLETED,
  "clin": AppointmentStatus.SCHEDULED,
  "conf": AppointmentStatus.SCHEDULED,
  "confphone": AppointmentStatus.SCHEDULED,
  "confsms": AppointmentStatus.SCHEDULED,
  "cxb": AppointmentStatus.CANCELLED,
  "delayed": AppointmentStatus.SCHEDULED,
  "early arr": AppointmentStatus.CHECKED_IN,
  "early/late": AppointmentStatus.CHECKED_IN,
  "ep-add on": AppointmentStatus.SCHEDULED,
  "failedmsg": AppointmentStatus.SCHEDULED,
  "ha check": AppointmentStatus.CHECKED_IN,
  "invalidins": AppointmentStatus.UNKNOWN,
  "kiosk": AppointmentStatus.CHECKED_IN,
  "late arr": AppointmentStatus.CHECKED_IN,
  "multivisit": AppointmentStatus.SCHEDULED,
  "n/a": AppointmentStatus.UNKNOWN,
  "nchr": AppointmentStatus.COMPLETED,
  "np-add on": AppointmentStatus.SCHEDULED,
  "n/s": AppointmentStatus.NO_SHOW,
  "n/s/b": AppointmentStatus.NO_SHOW,
  "nsb sleep": AppointmentStatus.NO_SHOW,
  "pen": AppointmentStatus.SCHEDULED,
  "pen_chkin": AppointmentStatus.SCHEDULED,
  "pend ref": AppointmentStatus.SCHEDULED,
  "pend/scan": AppointmentStatus.SCHEDULED,
  "pen_pay": AppointmentStatus.SCHEDULED,
  "precert": AppointmentStatus.SCHEDULED,
  "proc": AppointmentStatus.CHECKED_IN,
  "qr_arr": AppointmentStatus.CHECKED_IN,
  "r/s": AppointmentStatus.RESCHEDULED,
  "rsb": AppointmentStatus.RESCHEDULED,
  "rsp": AppointmentStatus.RESCHEDULED,
  "rsphone": AppointmentStatus.RESCHEDULED,
  "shot given": AppointmentStatus.COMPLETED,
  "sl add on": AppointmentStatus.SCHEDULED,
  "sl bill": AppointmentStatus.UNKNOWN,
  "sleep": AppointmentStatus.COMPLETED,
  "sl no bill": AppointmentStatus.UNKNOWN,
  "sms sent": AppointmentStatus.SCHEDULED,
  "stop front": AppointmentStatus.UNKNOWN,
  "voicemsg": AppointmentStatus.SCHEDULED,
};

export const ENTP_COBALT_APPOINTMENT_TYPE_TO_VISIT_TYPE_MAP: Partial<Record<AppointmentType, string>> = {
  [AppointmentType.NEW_PATIENT_CHECKIN]: "NP",
  [AppointmentType.INITIAL_CONSULTATION]: "NP",
  [AppointmentType.EXISTING_PATIENT_CHECKIN]: "EP",
  [AppointmentType.FOLLOW_UP]: "EP",
};

// These native values describe source provenance rather than clinical appointment semantics.
// Preserve them in the Scheduling Index without creating new Valerie appointments from them.
export const ENTP_NON_PROJECTABLE_ECW_VISIT_TYPE_MAP = {
  migrated: AppointmentType.UNKNOWN,
} satisfies Record<string, AppointmentType.UNKNOWN>;

export const ENTP_COBALT_VISIT_TYPE_MAP: Record<string, AppointmentType> = {
  "abr/vng": AppointmentType.PROCEDURE,
  "audio full": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "botox": AppointmentType.INJECTION,
  "challenges": AppointmentType.PROCEDURE,
  "chem caut": AppointmentType.PROCEDURE,
  "clin trial": AppointmentType.RESEARCH,
  "cosm f/u": AppointmentType.FOLLOW_UP,
  "cosmetic": AppointmentType.SPECIALIST_CONSULT,
  "cosmpo": AppointmentType.FOLLOW_UP,
  "countycare": AppointmentType.NEW_PATIENT_CHECKIN,
  "cpap rpm": AppointmentType.FOLLOW_UP,
  "cpap rslts": AppointmentType.FOLLOW_UP,
  "ct w inf": AppointmentType.PROCEDURE,
  "ct w/o inf": AppointmentType.PROCEDURE,
  "dispensev": AppointmentType.PROCEDURE,
  "dr/pa-tele": AppointmentType.TELEHEALTH,
  "drop off": AppointmentType.PROCEDURE,
  "drops": AppointmentType.PROCEDURE,
  "earmolds": AppointmentType.PROCEDURE,
  "ent med": AppointmentType.EXISTING_PATIENT_CHECKIN,
  // This native type is used for both new and established patients. Keep it ahead of its `EP EAR`
  // substring so legacy WebECW/Cobalt reads agree with the index's exact-code mapping.
  "new/ep ear": AppointmentType.NEW_OR_EXISTING_PATIENT_CHECKIN,
  "ep ear": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "ep stj": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "ep": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "er fu": AppointmentType.FOLLOW_UP,
  "f.f trauma": AppointmentType.URGENT_CARE,
  "f/u add on": AppointmentType.FOLLOW_UP,
  "ha check": AppointmentType.FOLLOW_UP,
  "ha dropoff": AppointmentType.PROCEDURE,
  "ha eval": AppointmentType.SPECIALIST_CONSULT,
  "ha fitting": AppointmentType.PROCEDURE,
  "ha pick up": AppointmentType.FOLLOW_UP,
  "haproblm": AppointmentType.FOLLOW_UP,
  "hearing": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "ime": AppointmentType.SPECIALIST_CONSULT,
  "in office": AppointmentType.PROCEDURE,
  "injection": AppointmentType.INJECTION,
  "inpt visit": AppointmentType.SPECIALIST_CONSULT,
  // Legacy WebECW/Cobalt substring matching must see the specific follow-up code first.
  "inspire fu": AppointmentType.FOLLOW_UP,
  "inspire": AppointmentType.SPECIALIST_CONSULT,
  "medicaid": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "mee": AppointmentType.SPECIALIST_CONSULT,
  ...ENTP_NON_PROJECTABLE_ECW_VISIT_TYPE_MAP,
  "new pt add": AppointmentType.NEW_PATIENT_CHECKIN,
  "new stj": AppointmentType.NEW_PATIENT_CHECKIN,
  "new vial": AppointmentType.INJECTION,
  "non-billss": AppointmentType.PROCEDURE,
  "np ears": AppointmentType.NEW_PATIENT_CHECKIN,
  "np inspire": AppointmentType.NEW_PATIENT_CHECKIN,
  "np trfcr": AppointmentType.NEW_PATIENT_CHECKIN,
  "np": AppointmentType.NEW_PATIENT_CHECKIN,
  // Office Visit does not distinguish a new patient from an established patient.
  "office visit": AppointmentType.NEW_OR_EXISTING_PATIENT_CHECKIN,
  "oa adj": AppointmentType.FOLLOW_UP,
  "oa consult": AppointmentType.SPECIALIST_CONSULT,
  "oa/dropoff": AppointmentType.PROCEDURE,
  "oa mailed": AppointmentType.PROCEDURE,
  "oa new": AppointmentType.NEW_PATIENT_CHECKIN,
  "oa repair": AppointmentType.PROCEDURE,
  "oa refit": AppointmentType.FOLLOW_UP,
  "oa/pickup": AppointmentType.FOLLOW_UP,
  // Online booking does not identify the visit's modality or whether the patient is new.
  "openaccess": AppointmentType.NEW_OR_EXISTING_PATIENT_CHECKIN,
  "pft": AppointmentType.PROCEDURE,
  "ph rem": AppointmentType.PROCEDURE,
  "ph study": AppointmentType.PROCEDURE,
  "po": AppointmentType.FOLLOW_UP,
  "procedure": AppointmentType.PROCEDURE,
  "ptvacc": AppointmentType.INJECTION,
  "reserved": AppointmentType.ROUTINE_CHECKUP,
  "ret ha": AppointmentType.FOLLOW_UP,
  "rp": AppointmentType.EXISTING_PATIENT_CHECKIN,
  "shots": AppointmentType.INJECTION,
  "shots bio": AppointmentType.INJECTION,
  "sl asvtitr": AppointmentType.PROCEDURE,
  "sl bipaptr": AppointmentType.PROCEDURE,
  "sl cpap": AppointmentType.PROCEDURE,
  "sl hme": AppointmentType.PROCEDURE,
  "sl hst": AppointmentType.PROCEDURE,
  "sl hst oa": AppointmentType.PROCEDURE,
  "sl hstinsp": AppointmentType.PROCEDURE,
  "sl hstppg": AppointmentType.PROCEDURE,
  "sl inspire": AppointmentType.PROCEDURE,
  "sl mslt": AppointmentType.PROCEDURE,
  "sl mwt": AppointmentType.PROCEDURE,
  "sl oa titr": AppointmentType.PROCEDURE,
  "sl ppg 2nt": AppointmentType.PROCEDURE,
  "sl profilr": AppointmentType.PROCEDURE,
  "sl psg": AppointmentType.PROCEDURE,
  "sl psgpeds": AppointmentType.PROCEDURE,
  "sl splnght": AppointmentType.PROCEDURE,
  "slit": AppointmentType.PROCEDURE,
  "sunohrec": AppointmentType.PROCEDURE,
  "tel-dox": AppointmentType.TELEHEALTH,
  "tele-pft": AppointmentType.TELEHEALTH,
  "televisit": AppointmentType.TELEHEALTH,
  "televsubo": AppointmentType.TELEHEALTH,
  "testing": AppointmentType.PROCEDURE,
  "tymp": AppointmentType.PROCEDURE,
  "tymp/oae": AppointmentType.PROCEDURE,
  "vaccine": AppointmentType.INJECTION,
  "vertigo": AppointmentType.TELEHEALTH,
  "zoc doc": AppointmentType.NEW_PATIENT_CHECKIN,
};

/**
 * Returns the upload classification for the ENT Partners group
 *
 * @param classification - The classification to get the upload classification for
 * @returns The upload classification for the ENT Partners group
 */
export function getEntpUploadClassification(classification: Classification): Classification {
  switch (classification) {
    case Classification.COVER_PAGE:
    case Classification.INSURANCE_CARD:
    case Classification.INTAKE:
    case Classification.IDENTIFICATION_CARD:
      return Classification.REFERRAL;
    default:
      return classification;
  }
}

export function getEntpClassificationsToProcess(
  classificationPageDict: ClassificationPageDict
): ClassificationPageDict {
  if (Classification.REFERRAL in classificationPageDict) {
    return { [Classification.REFERRAL]: classificationPageDict[Classification.REFERRAL] };
  }

  return classificationPageDict;
}

/**
 * Returns the classifications to extract for the ENT Partners group
 *
 * @returns The classifications to extract for the ENT Partners group
 */
export function getEntpClassificationToExtract(): Classification[] {
  return [
    Classification.ADMIN_LEAD_FORM,
    Classification.DME_ORDER,
    Classification.IDENTIFICATION_CARD,
    Classification.IMAGING_RESULTS,
    Classification.INSURANCE_CARD,
    Classification.INTAKE,
    Classification.LAB_RESULTS,
    Classification.PHARMACY_RELATED,
    Classification.PRIOR_AUTHORIZATION,
    Classification.PROCEDURE_SLEEP,
    Classification.REFERRAL,
    Classification.REFERER_INFORMATION,
    Classification.RESPONSE_TO_VH_REQUEST,
  ];
}

/**
 * Returns the classifications for document upload for the ENT Partners group
 *
 * @returns The classifications for document upload for the ENT Partners group
 */
export function getEntpClassificationForDocumentUpload(): Classification[] {
  return (Object.keys(ENTP_MAPPINGS) as Classification[]).filter((c) => c !== Classification.ADMIN_LEAD_FORM);
}

/**
 * Returns the workflows for the ENT Partners group
 *
 * @param group - The group to get the workflows for
 * @returns The workflows for the ENT Partners group
 */
export async function getEntpWorkflows(group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    ...DefaultWorkflow,
    [Workflow.DOC_MANAGEMENT]: getEntpClassificationForDocumentUpload(),
    [Workflow.LEAD_FORM]: [Classification.ADMIN_LEAD_FORM],
  };
}

const ENT_SERVICE_LINES = [
  ServiceLine.AUDIOLOGY,
  ServiceLine.ENT,
  ServiceLine.SLEEP,
  ServiceLine.TONSIL,
  ServiceLine.SINUS,
] as const;

const ENT_PARTNERS_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.MEMORIAL_DAY,
  HolidayNames.INDEPENDENCE_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.BLACK_FRIDAY,
  HolidayNames.CHRISTMAS_EVE,
  HolidayNames.CHRISTMAS_DAY,
  HolidayNames.NEW_YEARS_EVE,
] as const;

type EntpServiceLine = (typeof ENT_SERVICE_LINES)[number];

/**
 * Returns the service lines for the ENTP group
 *
 * @returns The service lines for the ENTP group
 */
export function getEntpServiceLines(): EntpServiceLine[] {
  return [...ENT_SERVICE_LINES];
}

/**
 * Returns the holidays for the ENT Partners group
 *
 * @returns The holidays for the ENT Partners group
 */
export function getEntpHolidays(): HolidayNames[] {
  return [...ENT_PARTNERS_HOLIDAYS];
}

const entpClassificationsForDisplay = [
  Classification.ADMIN_LEAD_FORM,
  Classification.BLANK,
  Classification.CARE_PLAN,
  Classification.CONSENT,
  Classification.COVER_PAGE,
  Classification.DME_ORDER,
  Classification.FINANCIAL,
  Classification.IDENTIFICATION_CARD,
  Classification.IMAGING_RESULTS,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.JUNK,
  Classification.LAB_RESULTS,
  Classification.LEGAL,
  Classification.MEDICAL_HISTORY,
  Classification.MEDICAL_RECORD_REQUEST,
  Classification.OTHER,
  Classification.PHARMACY_RELATED,
  Classification.PRIOR_AUTHORIZATION,
  Classification.PROCEDURE_SLEEP,
  Classification.PROGRESS_NOTE,
  Classification.REFERRAL,
  Classification.REFERRAL_STATUS_REQUEST,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
  Classification.UNKNOWN,
] as Classification[];

export function getEntpClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [
    {
      label: "Classifications",
      options: entpClassificationsForDisplay,
    },
  ];
}

export const ENTP_ENSO_EXCLUDED_LOCATIONS = ["SCI", "Baltimore ENT", "The ENT Specialists"];

export const ENTP_ENSO_FILTER_CONFIG = {
  filterOn: "location" as const,
  valuesToExclude: ENTP_ENSO_EXCLUDED_LOCATIONS,
};

export function getEntpScheduleConfigPath(): string {
  return entpartnersSchedulingConfig.policy.scheduleConfigPath;
}

export function getEntPartnersReferralRoutingStrategy(section: Section): ReferralRoutingStrategy[] {
  // CSV rows are spreadsheet-ingested with no scanned document, so skip the CloudCruise leg.
  if (isCsvContentType(section.contentType)) {
    return [ReferralRoutingStrategy.EHR];
  }

  return [ReferralRoutingStrategy.EHR_AND_CLOUDCRUISE];
}

export function isEntpCloudCruiseEnabled(section: Section): boolean {
  const classification = section.uploadClassification || section.classification;

  return (
    classification === Classification.LAB_RESULTS ||
    classification === Classification.IMAGING_RESULTS ||
    classification === Classification.PHARMACY_RELATED ||
    classification === Classification.PROCEDURE_SLEEP ||
    classification === Classification.DME_ORDER
  );
}

export async function getEntpAutocloseStatus(task: Task, section: Section): Promise<TaskStatus> {
  const classification = section.uploadClassification || section.classification;

  if (classification === Classification.REFERRAL || classification === Classification.PRIOR_AUTHORIZATION) {
    return TaskStatus.VERIFICATION_NEEDED;
  }

  if (classification === Classification.LAB_RESULTS || classification === Classification.PHARMACY_RELATED) {
    if (await isUuidSelected(task.id, 0.95)) {
      return TaskStatus.COMPLETED;
    }

    return TaskStatus.VERIFICATION_NEEDED;
  }

  if (isEntpCloudCruiseEnabled(section)) {
    return TaskStatus.VERIFICATION_NEEDED;
  }

  return TaskStatus.REVIEW_NEEDED;
}

export const ENTP_LANGUAGES_NOT_ACTIONABLE = new Set([
  PersonLanguage.ENGLISH.toLowerCase(),
  PersonLanguage.NOT_PROVIDED.toLowerCase(),
]);

export const ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_DEFAULT = "USER, REFERRAL";
export const ENTP_REFERRAL_TEL_ENC_ASSIGNMENT_SUB = "SUB Sanchez, Rafaela";
export const ENTP_DEMOGRAPHICS_TEL_ENC_ASSIGNMENT = "User, Front Desk";

export const ENTP_PROVIDER_NPIS_SUB = new Set(["1588753693", "1356350953", "1043752918"]);

export const ENTP_ADDRESS_SIMILARITY_THRESHOLD = 0.85;

export const ENT_PARTNERS_DOCUMENT_UPLOAD_PRACTITIONER_ID = "-IeImzx9UC5Q6WArw4qRaiQhsI3OB95z4MYIU-oWcFo";

export const ENTP_TIMEZONE = "America/Chicago";
export const ENTP_SLEEP_REASON_PATTERN =
  /(sleep|sllep|psg|ppg|hst|mslt|hsat|mwt|split.?night|\boatx?\b|cpap).*(results?|review)|(results?|review).*(sleep|sllep|psg|ppg|hst|mslt|hsat|mwt|split.?night|\boatx?\b|cpap)|televisit.*results?|results?.*televisit|follow.?up.*results?|results?.*follow.?up/i;

// eCW returns short encounter-status codes ("CANC", "N/S/B", "CHK", …), NOT the FHIR-style
// lowercase strings in the shared EncounterStatus enum. Evidence: WELL_CHILD_COMPLETED_STATUS
// ("CHK") in ecw-webecw-helpers.ts, and our own cancellation writes in
// resolveCancellationEhrUpdate use "CANC" / "N/S/B". Only two exclusions are supported by
// direct code evidence — extend from `logEncounterVocabulary` output if more surface in prod.
// (USHV/Heartplace precedent uses EncounterStatus enum values; those are latent no-ops.)
export const ENTP_WEBECW_ENCOUNTER_EXCLUSIONS: EcwEncounterExclusions = {
  statuses: ["CANC", "N/S/B", "R/S"],
  visitTypes: [EncounterType.MIGRATED, EncounterType.WEB],
};

// eCW visitType strings that represent a sleep diagnostic study (not a results-review visit).
// Extend from `logEncounterVocabulary` output as new types surface in production.
export const ENTP_SLEEP_DIAGNOSTIC_VISIT_TYPES: readonly string[] = ["PO", "SL PROFILR"];

export const ENTP_USER_TRIAGE_STAFF_ID = "91816";
export const ENTP_DME_CPAP_STAFF_ID = "323314";
export const ENTP_DME_SCHEDULER_STAFF_ID = "149971";

// Dr. Claire Kenneally's eCW doctorID (see referral-instructions.ts ENTP_PROVIDER_EHR_ID_DISPLAY_NAMES).
// Any encounter she renders counts as a qualifying DME encounter regardless of visitType/reason.
export const ENTP_DME_QUALIFYING_PROVIDER_ECW_ID = "140310";

// ECW IDs for Cobalt POST /telephone-encounters for DME orders.
// CHICAGO ENT - HALSTED (Cobalt GET /locations ehr_id=5)
export const ENTP_DME_TEL_ENC_LOCATION_ECW_ID = "5";
// CSC-Halsted sleep center resource (Cobalt GET /providers ehr_id=125239)
export const ENTP_DME_TEL_ENC_PROVIDER_ECW_ID = "125239";

export const ENTP_NAME_TO_STAFF_ID: Record<string, string> = {
  "User, Triage": ENTP_USER_TRIAGE_STAFF_ID,
  "USER, CPAP": ENTP_DME_CPAP_STAFF_ID,
  "USER, SLEEP RESULTS": "375813",
  "User, Allergy": "221901",
  "User, Scheduler": "149971",
  "SUB Sanchez, Rafaela": "270216",
  "Williams, Cara": "340618",
  "Cuzco, Janice": "113443",
  "Nikolayeva, Nina": "211736",
  "Gamez, Emily": "244572",
  "Grant, Laura": "136701",
  "Byrd, Stefanie": "328807",
  "VAL Sayiner, Sibel": "342640",
};

/** Michael Friedman — default rendering-provider fallback when no other provider resolves. */
export const ENTP_FRIEDMAN_NPI = "1932161056";

export const ENTP_NPI_TO_STAFF_ID: Record<string, string> = {
  "1417933946": "250817",
  "1508098476": "136540",
  "1922531334": "359519",
  "1366134041": "306006",
  "1609558030": "355422",
  "1497296800": "260468",
  [ENTP_FRIEDMAN_NPI]: "122",
  "1174554125": "9168",
  "1891709226": "252684",
  "1073724076": "344417",
  "1619171121": "150292",
  "1952521981": "140310",
  "1588753693": "277409",
  "1366625295": "9164",
  "1043752918": "311023",
  "1346640828": "136797",
  "1942650130": "253308",
  "1336621028": "211196",
  "1356350953": "277410",
  "1457623449": "229046",
  "1275521320": "359517",
  "1982022810": "224983",
  "1124713342": "384020",
  "1295797470": "9163",
  "1174297659": "379209",
};

export const ENTP_COBALT_FOLDER_BY_CLASSIFICATION: Partial<Record<Classification, string>> = {
  [Classification.REFERRAL]: "Referral",
  [Classification.DME_ORDER]: "SLEEP STUDY/DME",
  [Classification.PROCEDURE_SLEEP]: "SLEEP STUDY",
  [Classification.IMAGING_RESULTS]: "X-Ray Results",
  [Classification.LAB_RESULTS]: "Lab Results",
  [Classification.PHARMACY_RELATED]: "AUTHORIZATION/MEDICATION",
};

export function lookupEntpStaffId(name: string | null, npi: string | null): string | null {
  if (name) {
    const byName = ENTP_NAME_TO_STAFF_ID[name];

    if (byName) return byName;
  }

  if (npi) {
    const byNpi = ENTP_NPI_TO_STAFF_ID[npi];

    if (byNpi) return byNpi;
  }

  return null;
}

const ENTP_KNOWN_STAFF_IDS: ReadonlySet<string> = new Set([
  ...Object.values(ENTP_NAME_TO_STAFF_ID),
  ...Object.values(ENTP_NPI_TO_STAFF_ID),
]);

/**
 * Validates a staff ID surfaced by the Cobalt patient chart (rendering provider) against
 * the ENT Partners staff tables. Unknown IDs return null so the caller can fall through
 * to the next assignment tier rather than upload to an unmapped user.
 */
export function resolveEntpStaffIdFromCobalt(ehrId: string | null | undefined): string | null {
  if (!ehrId) return null;

  return ENTP_KNOWN_STAFF_IDS.has(ehrId) ? ehrId : null;
}

const ENTP_MISCELLANEOUS_EHR_ID = "-IeImzx9UC5Q6WArw4qRalmaJ1me3cN78JZKKSFihyM";

export function entpDocumentSubjectResolver(section: Section) {
  const classification = section.uploadClassification || section.classification;

  return (patient: Patient) => {
    if (isPatientNotInEhr(patient) && classification === Classification.PROCEDURE_SLEEP) {
      return { ehrSystemId: ENTP_MISCELLANEOUS_EHR_ID, mrn: null };
    }

    return { ehrSystemId: tryGetEhrSystemId(patient), mrn: tryGetMrn(patient) };
  };
}

export const ENTP_PA_MEDICATION_ASSIGNMENT: Record<string, string> = {
  shepard: "Cuzco, Janice",
  siddiqi: "Cuzco, Janice",
  kenneally: "User, Triage",
  gamez: "Gamez, Emily",
  kurzman: "SUB Sanchez, Rafaela",
  kakodkar: "SUB Sanchez, Rafaela",
  masella: "Grant, Laura",
  friedman: "User, Triage",
  levy: "User, Triage",
  venkatesan: "User, Triage",
  hudson: "User, Triage",
  colman: "User, Triage",
};

export const ENTP_PA_PROCEDURE_ASSIGNMENT: Record<string, string> = {
  kurzman: "SUB Sanchez, Rafaela",
  kakodkar: "SUB Sanchez, Rafaela",
  masella: "Grant, Laura",
};

export const ENTP_PA_OFFICE_VISIT_CPT_CODES = new Set([
  "99201",
  "99202",
  "99203",
  "99204",
  "99205",
  "99211",
  "99212",
  "99213",
  "99214",
  "99215",
]);

export function hasEntpOfficeVisitCptCodes(cptCodes: string[]): boolean {
  return cptCodes.some((code) => ENTP_PA_OFFICE_VISIT_CPT_CODES.has(code));
}

export type EntpPriorAuthDisposition = {
  providerAssignment: string | null;
  isMarkReviewed: boolean;
};

export function getEntpPriorAuthDisposition(priorAuth: PriorAuthorization | null): EntpPriorAuthDisposition {
  const status = priorAuth?.authorizationStatus ?? PriorAuthorizationStatus.OTHER;
  const type = priorAuth?.authorizationType ?? null;
  const providerFamilyName = priorAuth?.practitioner?.familyName?.toLowerCase() ?? null;
  const cptCodes: string[] = priorAuth?.commonProcedureTerminologyList?.map((cpt: { code: string }) => cpt.code) ?? [];

  if (hasEntpOfficeVisitCptCodes(cptCodes)) {
    if (status === PriorAuthorizationStatus.APPROVED) {
      return { providerAssignment: null, isMarkReviewed: true };
    }

    if (status === PriorAuthorizationStatus.DENIED) {
      return { providerAssignment: "User, Scheduler", isMarkReviewed: false };
    }
  }

  if (type === PriorAuthorizationType.PROCEDURE) {
    if (status === PriorAuthorizationStatus.APPROVED) {
      return { providerAssignment: null, isMarkReviewed: true };
    }

    return { providerAssignment: "Grant, Laura", isMarkReviewed: false };
  }

  if (type === PriorAuthorizationType.MEDICATION) {
    if (!providerFamilyName) {
      return { providerAssignment: null, isMarkReviewed: false };
    }

    return {
      providerAssignment: ENTP_PA_MEDICATION_ASSIGNMENT[providerFamilyName] ?? "User, Triage",
      isMarkReviewed: false,
    };
  }

  if (providerFamilyName && providerFamilyName in ENTP_PA_PROCEDURE_ASSIGNMENT) {
    return { providerAssignment: ENTP_PA_PROCEDURE_ASSIGNMENT[providerFamilyName], isMarkReviewed: false };
  }

  const staffAssignments = new Set<string>(
    cptCodes.map((code: string) => entpCptToStaff[code]).filter((staff): staff is string => staff != null)
  );

  if (staffAssignments.size === 1) {
    const assignment = [...staffAssignments][0];

    return { providerAssignment: assignment, isMarkReviewed: false };
  }

  return { providerAssignment: null, isMarkReviewed: false };
}

export type EntpInsuranceMappingEntry = EcwInsuranceMappingEntry;
export type EntpInsuranceMapping = EcwInsuranceMapping;

export const ENTP_INSURANCE_MAPPING: EntpInsuranceMapping = entpInsuranceMappingData as EntpInsuranceMapping;

function resolveEntpCoverageEcwId(coverage: Coverage | null, forPrimary: boolean): string | null {
  return resolveCoverageEcwId(coverage, ENTP_INSURANCE_MAPPING, forPrimary);
}

export type EntpPatientMetadataInsuranceVariables = PatientMetadataInsuranceVariables;

export function getEntpPatientMetadataInsuranceVariables(
  referral: Referral | null
): EntpPatientMetadataInsuranceVariables {
  const primary = referral?.primaryCoverage ?? null;
  const secondary = referral?.secondaryCoverage ?? null;
  const primaryEcwId = resolveEntpCoverageEcwId(primary, true);
  const secondaryEcwId = resolveEntpCoverageEcwId(secondary, false);

  return buildPatientMetadataInsuranceVariables(primary, secondary, primaryEcwId, secondaryEcwId);
}

export function getEntpOutsideBusinessHoursMessage(): string {
  return "Thank you for your message. Our office is currently closed due to being outside of business hours (M-F, 8:30 AM - 5:00 PM CT) or a holiday closure. To reach our on-call doctor, please call us at (773) 296-5500. If this is a medical emergency, please dial 911.";
}

/**
 * Builds the ECW filename for a DME order document.
 * @param lastVisitDateIso ISO timestamp of the patient's most recent qualifying sleep visit, or null if none exists.
 */
export function getEntpDmeFileName(section: Section, lastVisitDateIso: string | null): string | null {
  const dmeOrderType = section.reviewedContent?.dmeOrderType as DmeOrderType | null;

  if (!dmeOrderType) return null;

  const dateReceived = section.reviewedContent?.dateReceived as string | null;
  const datePrefix = dateReceived ? DateTime.fromISO(dateReceived, { zone: ENTP_TIMEZONE }).toFormat("yyyy.MM.dd") : "";

  if (dmeOrderType === DmeOrderType.SETUP) {
    return [datePrefix, "Setup"].filter(Boolean).join(" ");
  }

  if (lastVisitDateIso) {
    // Parse in ENTP's local zone so an evening visit doesn't shift forward a day under UTC.
    const lastVisitFormatted = DateTime.fromISO(lastVisitDateIso, { zone: ENTP_TIMEZONE }).toFormat("yyyy.MM.dd");

    return [datePrefix, `CMN - last visit on ${lastVisitFormatted}`].filter(Boolean).join(" ");
  }

  return [datePrefix, "CMN - new visit needed"].filter(Boolean).join(" ");
}

export const ENT_PARTNERS_AUTO_INTAKE_CONFIG: AutoIntakeConfig = {
  byClassification: {
    [Classification.REFERRAL]: {
      rate: 0.95,
      blockChecks: [
        {
          ...AutoIntakeBlockCheck.MATCHED_PRIMARY_COVERAGE_PAYER,
          passes: (has, initialReviewedFields) =>
            initialReviewedFields === null ||
            has("customerPayer") ||
            getNestedValue(initialReviewedFields, "coverageType") === CoverageType.SELF_PAY,
        },
      ],
    },
  },
  defaultPolicy: { rate: 0.95 },
};

export function getEntpMentDocRoutingSkipStatus(section: Section, patient: Patient): TaskStatus {
  if (isPatientNotInEhr(patient)) {
    const classification = section.uploadClassification || section.classification;

    return classification === Classification.PROCEDURE_SLEEP ? TaskStatus.REVIEW_NEEDED : TaskStatus.NOT_RELEVANT;
  }

  return TaskStatus.REVIEW_NEEDED;
}
